import React, { useState, useEffect } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    useGetInnovationPodByStudentIdQuery,
    useGetSprintsByStageIdQuery,
    useSubmitSprintEvidenceMutation,
    useLazyGetCourseProgressQuery,
    useGetInnovationPodByIdQuery
} from "@/APIConnect";
import { useFileUpload } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { PodLayout } from "./components/PodLayout";

const extractId = (id: any): string => {
    if (!id) return '';
    if (typeof id === 'string') return id;
    if (typeof id === 'object') {
        const { timestamp, machine, pid, increment } = id;
        if (timestamp !== undefined && machine !== undefined && pid !== undefined && increment !== undefined) {
            const h = (n: number, len: number) => (n >>> 0).toString(16).padStart(len, '0').slice(-len);
            return `${h(timestamp, 8)}${h(machine, 6)}${h(pid, 4)}${h(increment, 6)}`.toLowerCase();
        }
    }
    return String(id);
};

function SprintsDataCollector({ stageId, onSprintsLoaded }: { stageId: string, onSprintsLoaded: (stageId: string, sprints: any[]) => void }) {
    const { data } = useGetSprintsByStageIdQuery(stageId);
    useEffect(() => {
        if (data?.resultObject) {
            onSprintsLoaded(stageId, data.resultObject);
        }
    }, [data, stageId, onSprintsLoaded]);
    return null;
}

const PodWorkspace = () => {
    const { user } = useAuth0();
    const userId = user?.sub?.replace('auth0|', '') || '';
    const { toast } = useToast();
    const { uploadFile } = useFileUpload();
    const [submitEvidence] = useSubmitSprintEvidenceMutation();
    const [isUploading, setIsUploading] = useState(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const selectedCourseId = searchParams.get('courseId');
    const selectedClassroomId = searchParams.get('classroomId');
    const [innovationPodId, setInnovationPodId] = useState<string | null>(null);

    const [getCourseProgress, { isFetching: isProgressLoading }] = useLazyGetCourseProgressQuery();

    // Effect to fetch progress when URL params change
    useEffect(() => {
        const fetchProgress = async () => {
            if (selectedCourseId && selectedClassroomId) {
                try {
                    const progress = await getCourseProgress({ courseId: selectedCourseId, classroomId: selectedClassroomId }).unwrap();
                    const result = progress?.resultObject || progress;
                    setInnovationPodId(extractId(result?.innovationPodId) || null);
                } catch (err) {
                    console.error("Failed to fetch progress:", err);
                    setInnovationPodId(null);
                }
            }
        };
        fetchProgress();
    }, [selectedCourseId, selectedClassroomId]);

    const { data: idResponse, isLoading: idLoading } = useGetInnovationPodByIdQuery(innovationPodId, { skip: !innovationPodId });
    const { data: studentResponse, isLoading: studentLoading } = useGetInnovationPodByStudentIdQuery(userId, { skip: !userId || !!innovationPodId || !!selectedCourseId });

    // When a course is selected, only use the pod from that course's progress (or null if none)
    const rawPodData = selectedCourseId
        ? (innovationPodId ? idResponse?.resultObject : null)
        : (innovationPodId ? idResponse?.resultObject : studentResponse?.resultObject);
    const isPodLoading = idLoading || (!selectedCourseId && studentLoading) || isProgressLoading;

    // Normalize all stage IDs to strings so API calls and comparisons work correctly
    const podData = React.useMemo(() => {
        if (!rawPodData) return rawPodData;
        return {
            ...rawPodData,
            podStages: rawPodData.podStages?.map((stage: any) => ({
                ...stage,
                id: extractId(stage.id),
            })),
        };
    }, [rawPodData]);

    const [allSprints, setAllSprints] = useState<any[]>([]);
    const [selectedSprintId, setSelectedSprintId] = useState<string | null>(null);

    const handleSprintsLoaded = React.useCallback((stageId: string, stageName: string, sprints: any[]) => {
        setAllSprints(prev => {
            const otherSprints = prev.filter(s => s.stageId !== stageId);
            const stageSprints = sprints.map((s: any) => ({ ...s, id: extractId(s.id), stageId, stageName }));

            const updated = [...otherSprints, ...stageSprints].sort((a, b) => {
                const stageA = podData?.podStages?.findIndex((st: any) => st.id === a.stageId) ?? 0;
                const stageB = podData?.podStages?.findIndex((st: any) => st.id === b.stageId) ?? 0;
                if (stageA !== stageB) return stageA - stageB;
                return a.sprintOrder - b.sprintOrder;
            });

            if (JSON.stringify(prev) === JSON.stringify(updated)) return prev;
            return updated;
        });
    }, [podData]);

    useEffect(() => {
        if (allSprints.length > 0 && !selectedSprintId) {
            const firstIncomplete = allSprints.find((s: any) => {
                const isCompleted = s.status === 'VALIDATED' || s.sprintValidation?.decision === 'APPROVED';
                return !isCompleted;
            });
            setSelectedSprintId(firstIncomplete?.id || allSprints[0]?.id);
        }
    }, [allSprints, selectedSprintId]);

    const handleUploadEvidence = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !selectedSprintId) return;

        setIsUploading(true);
        try {
            const fileName = `sprint_evidence_${selectedSprintId}_${Date.now()}`;
            const fileUrl = await uploadFile(file, fileName);

            if (fileUrl) {
                await submitEvidence({
                    sprintId: selectedSprintId,
                    studentId: userId,
                    fileUrl: fileUrl
                }).unwrap();

                toast({
                    title: "Success",
                    description: "Evidence submitted successfully",
                });
            }
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to submit evidence. Please try again.",
                variant: "destructive"
            });
        } finally {
            setIsUploading(false);
        }
    };

    if (isPodLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F5F7FA]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#347468]"></div>
            </div>
        );
    }
    if (!podData) {
        return <PodLayout isEmpty><div /></PodLayout>;
    }

    const activeSprint = allSprints.find((s: any) => s.id === selectedSprintId);
    const isCompleted = activeSprint?.sprintValidation?.decision === 'APPROVED';

    return (
        <PodLayout activeTab="workspace" podData={podData}>
            {podData?.podStages?.map((stage: any) => (
                <SprintsDataCollector
                    key={stage.id}
                    stageId={stage.id}
                    onSprintsLoaded={(id, data) => handleSprintsLoaded(id, stage.stageName, data)}
                />
            ))}

            <input
                type="file"
                ref={fileInputRef}
                onChange={handleUploadEvidence}
                className="hidden"
            />

            <div className="ws">
                <div className="ws-left">
                    <div className="ws-lh">Pod Steps & Gates</div>
                    {podData?.podStages?.map((stage: any, index: number) => {
                        const isDone = stage.isCompleted;
                        const isCurrent = !isDone && podData?.podStages?.findIndex((s: any) => !s.isCompleted) === index;
                        const colorVar = isDone ? 'var(--teal)' : (isCurrent ? 'var(--peach)' : 'var(--border)');

                        return (
                            <React.Fragment key={stage.id}>
                                <div className="ws-step-group" style={{ opacity: isCurrent || isDone ? 1 : 0.45 }}>
                                    <div className="ws-step-label">
                                        <div className="ws-step-num" style={{ background: colorVar }}>
                                            {isDone ? '✓' : index + 1}
                                        </div>
                                        <div className="ws-step-nm" style={{ color: isDone || isCurrent ? colorVar : '' }}>{stage.stageName}</div>
                                    </div>
                                    {allSprints.filter(s => s.stageId === stage.id).map(sprint => {
                                        const isActive = sprint.id === selectedSprintId;
                                        const cIsDone = sprint.status === 'VALIDATED' || sprint.sprintValidation?.decision === 'APPROVED';

                                        return (
                                            <div
                                                key={sprint.id}
                                                className={`ws-gate ${isActive ? 'act' : ''}`}
                                                onClick={() => setSelectedSprintId(sprint.id)}
                                            >
                                                <div className="ws-gate-nm">{sprint.name}</div>
                                                <div className="ws-gate-proof">Sprint Task</div>
                                                <div className="ws-gate-st" style={{ color: cIsDone ? 'var(--teal)' : 'var(--gold)' }}>
                                                    {cIsDone ? (
                                                        <><svg width="7" height="7" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 3.5l2 2L6 1.5" /></svg>Validated</>
                                                    ) : (
                                                        <><svg width="7" height="7" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="3.5" cy="3.5" r="2.5" /></svg>Pending</>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {index < (podData?.podStages?.length - 1) && <div className={`ws-step-line ${isDone ? 'done' : ''}`}></div>}
                            </React.Fragment>
                        );
                    })}
                </div>
                <div className="ws-mid">
                    {activeSprint ? (
                        <>
                            <div className="ws-sh">
                                <span className="ws-pill">{activeSprint.stageName} · Task</span>
                                <span className="ws-st">{activeSprint.name}</span>
                            </div>
                            <div className="ws-fl">Assignment Focus</div>
                            <div className="ws-fv">{activeSprint.assignment?.title || 'No title'}</div>
                            <div className="ws-fl">Detailed Briefing</div>
                            <div className="ws-fv">{activeSprint.assignment?.assignmentText || 'No detailed instructions.'}</div>
                            {activeSprint.assignment?.fileUrl && (
                                <a href={activeSprint.assignment?.fileUrl} target="_blank" rel="noreferrer" className="text-[11px] font-bold" style={{ color: 'var(--teal)' }}>View Resource File</a>
                            )}
                            <div className="ws-ev">
                                <div className="ws-ev-h">
                                    <svg width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 1.5h4a.5.5 0 01.5.5v6l-2.5-1.5L2.5 8V2a.5.5 0 01.5-.5z" /></svg>Sprint Deliverable
                                </div>
                                <div className="ws-ev-r">
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div className="ws-ev-ic" style={{ background: isCompleted ? 'rgba(13,191,138,.1)' : '', color: isCompleted ? 'var(--teal)' : '' }}>
                                            <svg width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5 2v6M3 6l2 2 2-2" /></svg>
                                        </div>
                                        <div>
                                            <div className="ws-ev-nm">Sprint Submission</div>
                                            <div className="ws-ev-st">{isCompleted ? 'Verified' : 'Action Required'}</div>
                                        </div>
                                    </div>
                                    {activeSprint.sprintEvidence?.fileUrl ? (
                                        <button className="ws-upl" onClick={() => window.open(activeSprint.sprintEvidence.fileUrl, '_blank')}>View File</button>
                                    ) : (
                                        <button className="ws-upl" onClick={() => fileInputRef.current?.click()} disabled={isUploading}>
                                            <svg width="9" height="9" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4.5 6V1.5M2.5 3.5l2-2 2 2" /><path d="M1 7h7" /></svg>
                                            {isUploading ? 'Uploading...' : 'Upload'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div style={{ textAlign: 'center', color: 'var(--text-4)' }}>Select a sprint to view details</div>
                    )}
                </div>
                <div className="ws-right">
                    <div className="ws-rh">Signals</div>
                    <div style={{ marginBottom: '10px' }}>
                        <div className="text-[13.5px] font-bold" style={{ color: 'var(--text-3)', marginBottom: '3px' }}>MENTOR</div>
                        <div className="ws-si"><div className="ws-sd" style={{ background: 'var(--peach)' }}></div>Are we missing risks?</div>
                        <div className="ws-si"><div className="ws-sd" style={{ background: 'var(--sky)' }}></div>Is this viable?</div>
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                        <div className="text-[13.5px] font-bold" style={{ color: 'var(--text-3)', marginBottom: '3px' }}>INNOVATION</div>
                        <div className="ws-tags"><span className="ws-tag" style={{ background: 'var(--peach-glow)', color: 'var(--peach)' }}>Adoption Risk</span><span className="ws-tag" style={{ background: 'var(--teal-glow)', color: 'var(--teal)' }}>Completed</span></div>
                    </div>
                    <div>
                        <div className="text-[13.5px] font-bold" style={{ color: 'var(--text-3)', marginBottom: '3px' }}>VALIDATION</div>
                        <div className="ws-vi"><span style={{ color: 'var(--teal)' }}>AI check</span><span className="text-[9px] font-semibold" style={{ color: 'var(--teal)' }}>Done</span></div>
                        <div className="ws-vi"><span style={{ color: 'var(--text-3)' }}>Instructor</span><span className="text-[9px] font-semibold" style={{ color: 'var(--gold)' }}>Pending</span></div>
                        <div className="ws-vi"><span style={{ color: 'var(--text-3)' }}>Industry</span><span className="text-[9px] font-semibold" style={{ color: 'var(--text-4)' }}>Locked</span></div>
                    </div>
                    <button className="ws-mb" onClick={() => navigate('/innovation-pod/pod-room')}>
                        <svg width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5 1c2.5 0 4 1.5 4 3s-1.5 3-4 3" /></svg>Message Mentor
                    </button>
                </div>
            </div>
        </PodLayout>
    );
};

export default PodWorkspace;
